"use client";

import { ReactNode, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { locales } from "@/i18n/locale";
import HeroBg from "@/components/blocks/hero/bg";

type LandingThemeProps = {
  children: ReactNode;
  className?: string;
};

const LANDING_SECTIONS = new Set([
  "posts",
  "pricing",
  "my-orders",
  "my-credits",
  "my-invites",
  "api-keys",
  "styles",
]);

const isLandingPath = (pathname: string | null) => {
  if (!pathname) return false;
  const normalized = pathname.replace(/\/$/, "");
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) return true;
  const firstSegment = segments[0];
  const hasLocalePrefix = locales.includes(firstSegment);
  if (segments.length === 1 && hasLocalePrefix) return true;
  const contentSegments = hasLocalePrefix ? segments.slice(1) : segments;
  if (contentSegments.length === 0) return true;
  if (LANDING_SECTIONS.has(contentSegments[0])) return true;
  return false;
};

const getLandingTheme = (_pathname: string | null) => "landing-rankpress dark";

export default function LandingTheme({ children, className }: LandingThemeProps) {
  const pathname = usePathname();
  const isLanding = isLandingPath(pathname);
  const landingClassName = useMemo(
    () => cn(getLandingTheme(pathname), className),
    [pathname, className]
  );
  const landingWrapperClassName = useMemo(
    () => cn(landingClassName, "relative"),
    [landingClassName]
  );

  useEffect(() => {
    if (!isLanding) return;
    const bodyClasses = landingClassName.split(" ").filter(Boolean);
    const body = document.body;
    body.classList.add(...bodyClasses);
    return () => {
      body.classList.remove(...bodyClasses);
    };
  }, [isLanding, landingClassName]);

  return (
    <div className={isLanding ? cn(landingWrapperClassName, "overflow-x-hidden") : ""}>
      {isLanding ? <HeroBg /> : null}
      {children}
    </div>
  );
}
